import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { FeatureFlagKey, type FeatureFlagMap } from "encamp-shared/src/featureFlags";
import { Permission } from "generated-graphql/graphql";
import React, { useMemo } from "react";

import { forceStartReplayAndGetLink } from "../sentry";
import { commandCopy, commandOpen } from "./commandPalette/commandBuilders";
import { templateLinearTicket, buildLinearNewIssueUrl } from "./commandPalette/linear";
import { useRegisterCommands, type Command } from "./CommandPaletteRegistry";
import { LinearIcon } from "./LinearIcon";

type DerivedContext = {
  tenantId?: string;
  encodedTenantId?: string;
  facilityId?: string;
  facilityDisplay?: string;
  encodedFacilityId?: string;
  reportId?: string;
  encodedReportId?: string;
};

export function DefaultCommandRegistrations({
  derived,
  tenantLabel,
  navigate,
  getAppBaseUrl,
  copyToClipboard,
  copyRichLink,
  isRealStaff,
  hasPermissions,
  featureFlags,
}: {
  derived: DerivedContext;
  tenantLabel: string;
  navigate: (path: string) => void;
  getAppBaseUrl: () => string;
  copyToClipboard: (t: string) => Promise<void> | void;
  copyRichLink: (u: string, l: string) => Promise<void> | void;
  /** True for staff NOT emulating a customer — mirrors the sidebar's staff-section gate. */
  isRealStaff: boolean;
  hasPermissions: (permissions: Permission[]) => boolean;
  featureFlags: FeatureFlagMap;
}): React.ReactElement | null {
  const hrefDep = typeof window !== "undefined" ? window.location.href : getAppBaseUrl();

  // "Copy link to current page" command (always available, registered first to appear at top)
  const rawTitle = document.title;
  const titlePrefix = "Encamp / ";
  const strippedTitle = rawTitle.startsWith(titlePrefix)
    ? rawTitle.slice(titlePrefix.length) || rawTitle
    : rawTitle;
  const pageLinkCmds = useMemo<Command[]>(
    () => [
      {
        id: "copy-page-link",
        label: "Copy link to current page",
        description: strippedTitle,
        type: "copy-link",
        keywords: ["copy", "page", "link", "url"],
        signature: `page-link:${hrefDep}:${strippedTitle}`,
        run: () => {
          const raw = document.title;
          const prefix = "Encamp / ";
          const label = raw.startsWith(prefix) ? raw.slice(prefix.length) || raw : raw;
          copyRichLink(window.location.href, label);
        },
      },
    ],
    [hrefDep, strippedTitle, copyRichLink]
  );

  useRegisterCommands(pageLinkCmds, [pageLinkCmds], "page-link");

  // Staff fulfillment deep links. These navigate below the sidebar's
  // "Fulfillment" entry, so they follow the same gate as the sidebar's Staff
  // section: real (non-emulating) staff only.
  const staffNavCmds = useMemo<Command[]>(() => {
    if (!isRealStaff) {
      return [];
    }
    return [
      commandOpen({
        id: "nav-fulfillment-facilities",
        label: "Go to Chemical Facilities",
        path: "/staff/fulfillment/facilities",
        navigate,
        description: "/staff/fulfillment/facilities",
        signature: "nav:fulfillment-facilities",
        keywords: ["fulfillment", "facilities", "chemical"],
      }),
      commandOpen({
        id: "nav-fulfillment-workflows",
        label: "Go to Workflows",
        path: "/staff/fulfillment/workflows",
        navigate,
        description: "/staff/fulfillment/workflows",
        signature: "nav:fulfillment-workflows",
        keywords: ["fulfillment", "workflows"],
      }),
      commandOpen({
        id: "nav-fulfillment-wait-for-all",
        label: "Go to Wait For All",
        path: "/staff/fulfillment/wait-for-all",
        navigate,
        description: "/staff/fulfillment/wait-for-all",
        signature: "nav:fulfillment-wait-for-all",
        keywords: ["fulfillment", "wait"],
      }),
      commandOpen({
        id: "nav-fulfillment-error-triage",
        label: "Go to Error Triage",
        path: "/staff/fulfillment/error-clustering",
        navigate,
        description: "/staff/fulfillment/error-clustering",
        signature: "nav:fulfillment-error-triage",
        keywords: ["fulfillment", "error", "triage", "clustering"],
      }),
      commandOpen({
        id: "nav-fulfillment-waste",
        label: "Go to Waste Submissions",
        path: "/staff/fulfillment/waste",
        navigate,
        description: "/staff/fulfillment/waste",
        signature: "nav:fulfillment-waste",
        keywords: ["fulfillment", "waste", "submissions"],
      }),
    ];
  }, [navigate, isRealStaff]);

  useRegisterCommands(staffNavCmds, [staffNavCmds], "staff-nav");

  // Notifications navigation — available regardless of tenant context because
  // notifications are user-centric (the bell, inbox, and detail pages all live
  // at /notifications, gated by the FeatureFlagKey.Notifications flag at the
  // route level). Each tile-view also gets its own command so power users can
  // jump straight to the filtered list without two clicks.
  const notificationsEnabled = Boolean(featureFlags[FeatureFlagKey.Notifications]);

  // Chemical-report deep links mirror the sidebar's Reports entry, which only
  // exists when the chemicals module is enabled AND the user can route to
  // EPCRA reports.
  const canSeeChemicalReports =
    Boolean(featureFlags[FeatureFlagKey.TierIIModule]) &&
    hasPermissions([Permission.RouteEpcraReports]);
  const notificationNavCmds = useMemo<Command[]>(() => {
    if (!notificationsEnabled) {
      return [];
    }
    return [
      commandOpen({
        id: "nav-notifications",
        label: "Go to Notifications",
        path: "/notifications",
        navigate,
        description: "/notifications",
        signature: "nav:notifications",
        keywords: ["notifications", "inbox", "alerts", "bell"],
      }),
      commandOpen({
        id: "nav-notifications-aging",
        label: "Open Aging notifications",
        path: "/notifications?view=AGING",
        navigate,
        description: "Unread for more than 3 days, all organizations",
        signature: "nav:notifications:aging",
        keywords: ["notifications", "aging", "old", "stale"],
      }),
      commandOpen({
        id: "nav-notifications-unread",
        label: "Open Unread notifications",
        path: "/notifications?view=UNREAD",
        navigate,
        description: "Everything you have not opened, all organizations",
        signature: "nav:notifications:unread",
        keywords: ["notifications", "unread", "inbox"],
      }),
    ];
  }, [navigate, notificationsEnabled]);

  useRegisterCommands(notificationNavCmds, [notificationNavCmds], "notifications-nav");

  // Tenant default commands
  const tenantCmds = useMemo<Command[]>(() => {
    const items: Command[] = [];
    if (derived.tenantId && derived.encodedTenantId) {
      const tenantPath = `/o/${derived.encodedTenantId}`;
      const fullTenantUrl = `${getAppBaseUrl()}${tenantPath}`;
      const staffImplementationPath = `/staff/implementation/${derived.encodedTenantId}`;
      items.push(
        {
          id: "copy-tenant-link",
          label: `Copy link to tenant ${tenantLabel}`,
          description: tenantLabel,
          type: "copy-link",
          keywords: ["tenant", "link", "url", "copy"],
          signature: `tenant-link:${tenantPath}:${tenantLabel}`,
          run: () => copyRichLink(fullTenantUrl, tenantLabel),
        },
        commandCopy({
          id: "copy-tenant-url",
          idKind: "tenant URL",
          value: fullTenantUrl,
          copy: copyToClipboard,
          description: tenantPath,
          signature: `tenant-url:${tenantPath}`,
          keywords: ["tenant", "url", "copy"],
        }),
        commandCopy({
          id: "copy-tenant-id",
          idKind: "tenant ID",
          value: derived.tenantId ?? "",
          copy: copyToClipboard,
          signature: `tenant-id:${derived.tenantId ?? ""}`,
          keywords: ["tenant", "id", "copy"],
        })
      );

      if (isRealStaff) {
        items.push(
          commandOpen({
            id: "open-staff-implementation",
            label: "Open staff implementation for tenant",
            path: staffImplementationPath,
            navigate,
            description: staffImplementationPath,
            signature: `staff-implementation:${staffImplementationPath}`,
            keywords: ["staff", "implementation", "tenant"],
          })
        );
      }

      if (!derived.facilityId && !derived.reportId) {
        if (canSeeChemicalReports) {
          const reportsBase = `/o/${derived.encodedTenantId}/chemicals/reports`;
          const reportsOmnisearch = encodeURIComponent(
            `reportingYear:${currentTierIIReportingYear}`
          );
          const reportsPath = `${reportsBase}?omnisearch=${reportsOmnisearch}`;
          items.push(
            commandOpen({
              id: "open-tenant-reports-current-year",
              label: "Open current year chemical reports",
              path: reportsPath,
              navigate,
              description: reportsPath,
              signature: `tenant-reports:${reportsBase}?${reportsOmnisearch}`,
              keywords: ["tenant", "reports", "chemical", "current year"],
            })
          );
        }
      }
    }
    return items;
  }, [
    derived.tenantId,
    derived.encodedTenantId,
    derived.facilityId,
    derived.reportId,
    tenantLabel,
    navigate,
    getAppBaseUrl,
    copyToClipboard,
    copyRichLink,
    isRealStaff,
    canSeeChemicalReports,
  ]);

  useRegisterCommands(tenantCmds, [tenantCmds], "default-tenant");

  // Facility default commands
  const facilityCmds = useMemo<Command[]>(() => {
    const items: Command[] = [];
    if (derived.encodedTenantId && derived.encodedFacilityId && derived.facilityId) {
      const facilityProfilePath = `/o/${derived.encodedTenantId}/facilities/${derived.encodedFacilityId}/profile`;
      const facilityDisplayLabel = derived.facilityDisplay ?? String(derived.facilityId);
      const facilityProfileUrl = `${getAppBaseUrl()}${facilityProfilePath}`;
      const inventoryPath = `/o/${derived.encodedTenantId}/chemicals/inventory/${derived.encodedFacilityId}`;
      const wastePath = `/o/${derived.encodedTenantId}/waste/facilities/${derived.encodedFacilityId}`;

      items.push(
        {
          id: "copy-facility-link",
          label: `Copy link to facility ${facilityDisplayLabel}`,
          description: facilityDisplayLabel,
          type: "copy-link",
          keywords: ["facility", "link", "url", "copy"],
          signature: `facility-link:${facilityProfilePath}:${facilityDisplayLabel}`,
          run: () => copyRichLink(facilityProfileUrl, facilityDisplayLabel),
        },
        commandCopy({
          id: "copy-facility-url",
          idKind: "facility URL",
          value: facilityProfileUrl,
          copy: copyToClipboard,
          description: facilityProfilePath,
          signature: `facility-url:${facilityProfilePath}`,
          keywords: ["facility", "url", "copy"],
        }),
        commandCopy({
          id: "copy-facility-id",
          idKind: "facility ID",
          value: derived.facilityId,
          copy: copyToClipboard,
          signature: `facility-id:${derived.facilityId ?? ""}`,
          keywords: ["facility", "id", "copy"],
        })
      );

      if (hasPermissions([Permission.RouteFacilities])) {
        items.push(
          commandOpen({
            id: "open-facility-profile",
            label: "Open facility profile",
            path: facilityProfilePath,
            navigate,
            description: facilityProfilePath,
            signature: `facility-profile:${facilityProfilePath}`,
            keywords: ["facility", "profile", "open"],
          })
        );
      }

      if (featureFlags[FeatureFlagKey.TierIIModule]) {
        items.push(
          commandOpen({
            id: "open-facility-inventory",
            label: "Open facility chemical inventory",
            path: inventoryPath,
            navigate,
            description: inventoryPath,
            signature: `facility-inventory:${inventoryPath}`,
            keywords: ["facility", "inventory", "chemical", "open"],
          })
        );
      }

      if (featureFlags[FeatureFlagKey.WasteModule]) {
        items.push(
          commandOpen({
            id: "open-facility-waste",
            label: "Open facility waste",
            path: wastePath,
            navigate,
            description: wastePath,
            signature: `facility-waste:${wastePath}`,
            keywords: ["facility", "waste", "open"],
          })
        );
      }

      if (canSeeChemicalReports) {
        const customerReportsPath = `/o/${derived.encodedTenantId}/chemicals/reports`;
        const customerOmnisearch = encodeURIComponent(
          `facilityId:${derived.facilityId} reportingYear:${currentTierIIReportingYear}`
        );
        const customerReportsFullPath = `${customerReportsPath}?omnisearch=${customerOmnisearch}`;
        items.push(
          commandOpen({
            id: "open-customer-reports-facility",
            label: "Open current year chemical reports for facility",
            path: customerReportsFullPath,
            navigate,
            description: customerReportsFullPath,
            signature: `facility-reports:${customerReportsPath}?${customerOmnisearch}`,
            keywords: ["facility", "reports", "chemical", "current year", "open"],
          })
        );
      }

      if (!derived.reportId && isRealStaff) {
        const staffFacilityPath = `/staff/fulfillment/f/${derived.encodedFacilityId}/${currentTierIIReportingYear}`;
        items.push(
          commandOpen({
            id: "open-staff-fulfillment-facility",
            label: "Open current year chemical report staff fulfillment",
            path: staffFacilityPath,
            navigate,
            description: staffFacilityPath,
            signature: `staff-facility-fulfillment:${staffFacilityPath}`,
            keywords: ["facility", "fulfillment", "staff", "chemical report", "open"],
          })
        );
      }
    }
    return items;
  }, [
    derived.encodedTenantId,
    derived.encodedFacilityId,
    derived.facilityId,
    derived.facilityDisplay,
    derived.reportId,
    navigate,
    getAppBaseUrl,
    copyToClipboard,
    copyRichLink,
    hasPermissions,
    featureFlags,
    isRealStaff,
    canSeeChemicalReports,
  ]);

  useRegisterCommands(facilityCmds, [facilityCmds], "default-facility");

  // Report default commands
  const reportCmds = useMemo<Command[]>(() => {
    const items: Command[] = [];
    if (derived.encodedTenantId && derived.encodedReportId && derived.reportId) {
      if (canSeeChemicalReports) {
        const reportStepperPath = `/o/${derived.encodedTenantId}/chemicals/reports/${derived.encodedReportId}`;
        items.push(
          commandOpen({
            id: "open-report-stepper",
            label: "Open chemical report",
            path: reportStepperPath,
            navigate,
            description: reportStepperPath,
            signature: `report-stepper:${reportStepperPath}`,
            keywords: ["report", "chemical", "open", "stepper"],
          })
        );
      }
      items.push(
        commandCopy({
          id: "copy-report-id",
          idKind: "report ID",
          value: derived.reportId,
          copy: copyToClipboard,
          signature: `report-id:${derived.reportId ?? ""}`,
          keywords: ["report", "id", "copy"],
        })
      );
    }
    if (derived.encodedReportId && isRealStaff) {
      const fulfillmentReportPath = `/staff/fulfillment/r/${derived.encodedReportId}`;
      items.push(
        commandOpen({
          id: "open-report-fulfillment",
          label: "Open chemical report staff fulfillment",
          path: fulfillmentReportPath,
          navigate,
          description: fulfillmentReportPath,
          signature: `report-fulfillment:${fulfillmentReportPath}`,
          keywords: ["report", "fulfillment", "staff", "chemical", "open"],
        })
      );
    }
    return items;
  }, [
    derived.encodedTenantId,
    derived.encodedReportId,
    derived.reportId,
    navigate,
    copyToClipboard,
    canSeeChemicalReports,
    isRealStaff,
  ]);

  useRegisterCommands(reportCmds, [reportCmds], "default-report");

  // Linear issue creation command (prefilled via URL)
  const envDep = import.meta.env.VITE_ENV ?? "unknown";
  const linearIssueCmds = useMemo<Command[]>(() => {
    const items: Command[] = [];
    const envLabel = envDep;
    const origin = getAppBaseUrl();
    const currentHref = hrefDep;
    const buildFacilityProfileUrl = () => {
      if (!derived.encodedTenantId || !derived.encodedFacilityId) return undefined;
      return `/o/${derived.encodedTenantId}/facilities/${derived.encodedFacilityId}/profile`;
    };
    items.push({
      id: "linear-create-issue",
      label: "Create Linear issue",
      description: "Opens Linear with prefilled description and context",
      icon: <LinearIcon fontSize="small" color="action" />,
      keywords: ["linear", "issue", "bug", "ticket", "create"],
      run: () => {
        // Force start a replay if one isn't already recording
        const sentryReplayUrl = forceStartReplayAndGetLink();

        const href = currentHref;
        const facilityProfile = buildFacilityProfileUrl();
        const facilityProfileFull =
          derived.facilityId && facilityProfile ? `${origin}${facilityProfile}` : undefined;
        const pageTitle =
          typeof document !== "undefined" && document.title ? document.title : undefined;
        const { title: titleBase, description } = templateLinearTicket({
          href,
          envLabel,
          tenantLabel,
          tenantId: derived.tenantId,
          facilityDisplay:
            derived.facilityDisplay ??
            (derived.facilityId ? String(derived.facilityId) : undefined),
          facilityProfileUrl: facilityProfileFull,
          reportId: derived.reportId,
          sentryReplayUrl,
          pageTitle,
        });

        const url = buildLinearNewIssueUrl({ title: titleBase, description });
        if (typeof window !== "undefined") {
          window.open(url, "_blank", "noopener");
        }
      },
      signature: [
        envLabel,
        currentHref,
        tenantLabel ?? "",
        derived.tenantId ?? "",
        derived.facilityDisplay ?? "",
        buildFacilityProfileUrl() ?? "",
        derived.reportId ?? "",
      ].join("|"),
    });
    return items;
  }, [
    derived.encodedFacilityId,
    derived.encodedTenantId,
    derived.facilityDisplay,
    derived.facilityId,
    derived.reportId,
    derived.tenantId,
    getAppBaseUrl,
    tenantLabel,
    // ensure updates on env and URL changes
    envDep,
    hrefDep,
  ]);

  useRegisterCommands(linearIssueCmds, [linearIssueCmds], "default-linear");

  return null;
}
